/**
 * Detects whether a route is displaying draft / label-negotiation content —
 * i.e. a customer's proprietary, unreleased draft label or the comment
 * threads attached to it.
 *
 * Why this exists (spec §5.10): ``FeedbackButton`` captures a full-page
 * screenshot on every click and uploads it to a SHARED, cross-org GCS
 * bucket (``label-doctor-feedback-screenshots``) that Notion and other
 * consumers read via long-lived signed URLs. A pixel-perfect screenshot of
 * a draft label bypasses row-level-security entirely — RLS only protects
 * the database, not an image blob in a shared bucket.
 *
 * The realistic threat here is *accidental* egress by a legitimate user who
 * happens to click Feedback while a draft is on screen, not an adversary
 * exfiltrating their own org's data (they can already see it). So this
 * client-side check genuinely closes the gap for the common case — it is
 * not, and is not meant to be, an adversarial security boundary (a modified
 * client could always skip this check; see the server-side second net in
 * ``feedback_service.py::create``).
 *
 * Keep this list in sync with every surface that can render draft content
 * (verified against the live route/query-param contracts, not the dead
 * ``useVersionState`` hook's ``versionId``/``type`` params, which are
 * unused by ``DrugDetailPage.tsx`` at runtime):
 *
 *   - `/manage-drafts`                       — draft list/metadata (ManageDraftsPage).
 *   - `/drug/:id?source=draft&version=...`   — draft label body rendered by
 *     DrugDetailPage (OnlyOfficeDraftViewer / DraftContentRenderer). A bare
 *     `?version=...` with no `source` param is legacy shorthand for the same
 *     thing — see DrugDetailPage.tsx's
 *     `sourceParam === 'draft' || !sourceParam` branch and
 *     `handleUnifiedVersionChange`, which keeps `source=draft` in sync with
 *     the on-screen version on every change.
 *   - `/?searchMode=draft_review` (or legacy `?draftReview=true`) — the
 *     "Label Negotiations" comment table on SearchPage (DraftCommentsTable).
 *   - `/?labelNegotiations=true` — additive flag that merges `draft_results`
 *     into normal label search results on SearchPage.
 *
 * TODO(tenancy §5.10): this route matcher FAILS OPEN — any draft surface it
 * does not list gets screenshotted and shipped to the shared bucket. Two such
 * surfaces exist today and are NOT covered:
 *
 *   - `/research/:jobId` when the job's corpus is `label_negotiations`
 *     (ResearchResultPage renders `draft_results`). The corpus is only known
 *     after the job detail loads via API — it is not in the URL, so a
 *     synchronous route check cannot see it.
 *   - DrugDetailPage's version-comparison modal (VersionComparisonModal) can
 *     render a draft's diff while the primary URL still shows an FDA or
 *     historical `source` — the modal does not update the URL.
 *
 * Fix shape: invert the check so it fails CLOSED. Introduce a
 * `DraftContentBoundary` React context that components which actually RENDER
 * draft content set while mounted (ResearchResultPage when
 * `corpus === 'label_negotiations'`, VersionComparisonModal on a draft diff,
 * the draft viewer, the negotiation search results). `FeedbackButton` reads
 * that flag instead of parsing the path. A new draft surface that forgets to
 * opt in then yields no screenshot, rather than silently leaking one. This
 * function can then be deleted.
 */
export function isDraftSurface(pathname: string, search: string = ''): boolean {
  if (pathname === '/manage-drafts') {
    return true;
  }

  const params = new URLSearchParams(search);

  if (pathname.startsWith('/drug/')) {
    const source = params.get('source');
    const hasVersion = params.has('version');
    return hasVersion && (source === 'draft' || !source);
  }

  if (pathname === '/') {
    if (params.get('searchMode') === 'draft_review') return true;
    if (params.get('draftReview') === 'true') return true;
    if (params.get('labelNegotiations') === 'true') return true;
    return false;
  }

  return false;
}
